<script setup lang="ts">
	import circlePolygon from '@/helpers/circlePolygon'
	import currencyToInt from '@/helpers/currencyToInt'
	import { useHotelsStore } from '@/stores/hotels'
	import {
		MapboxMap,
		MapboxMarker,
		MapboxNavigationControl,
	} from '@studiometa/vue-mapbox-gl'
	import { storeToRefs } from 'pinia'
	import { computed, ref, watch } from 'vue'
	import { useToast } from 'vue-toastification'

	import { useAuth } from '@/composables/useAuth'
	import { useReverseGeocode } from '@/composables/useReverseGeocode'
	import type { GeocodeValidationResult } from '@/composables/useReverseGeocode'

	import { User } from '@/modules/users/interfaces/user'

	import HotelCustomMarker from '../components/HotelCustomMarker.vue'
	import useHotels from '../composables/useHotels'
	import { usePlaceDetails } from '../composables/usePlaceDetails'
	import type { Hotel } from '../interfaces/hotel'

	interface Props {
		displayMap: boolean
		showRadiusLabel?: boolean
		availabilityFormRef: any
		itemRefs: { hotelId: string; hotelContainer: HTMLElement | null }[]
	}

	const props = withDefaults(defineProps<Props>(), { showRadiusLabel: true })

	const toast = useToast()
	const { user } = useAuth()
	const { form, selectedHotel, filteredHotelList, searchContext } =
		storeToRefs(useHotelsStore())
	const { setSelectedHotel, setSearchContext } = useHotelsStore()
	const searchRadiusKm = computed(() => searchContext.value?.radiusKm)

	const { setFormValue, refetch, reservationNights } = useHotels()

	const {
		getGeocode,
		isFetching,
		paramsGeo,
		refetch: refetchGeocode,
	} = useReverseGeocode({
		lat: Number(form.value.latitude),
		lng: Number(form.value.longitude),
	})

	let markerIdCounter = 0
	const token = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN
	const mapboxRef = ref<{ map: any } | null>(null)
	const displayHotels = ref(true)
	const currentZoom = ref(12)
	const clusterMaxZoom = 14
	// Capas del canvas que representan hoteles (clic propio, no «buscar aquí»)
	const HOTEL_LAYERS = ['clusters', 'unclustered-hotels']
	const hotelItems = ref<{ [key: string]: boolean }>({})
	const customMarkers = ref<{ lng: number; lat: number; id: number }[]>([])
	const isSearchingInMap = ref(false)
	const isSearchMinimized = ref(false)
	const showConfirmModal = ref(false)
	const pendingClickCoords = ref<{ lng: number; lat: number } | null>(null)

	const shouldShowMarkers = computed(() => {
		return currentZoom.value >= clusterMaxZoom
	})

	const hotelsWithValidCoordinates = computed(() => {
		return filteredHotelList.value.filter((hotel) => {
			const lng = parseFloat(hotel.longitude)
			const lat = parseFloat(hotel.latitude)
			return !isNaN(lng) && !isNaN(lat) && !(lng === 0 && lat === 0)
		})
	})

	// Alto de un marcador de precio + respiro, para apilar los que comparten punto
	const MARKER_STACK_GAP = 52

	// Hoteles con la misma coordenada (torres de un mismo complejo, o el
	// proveedor que manda la del centro de la zona) caían en el mismo pixel:
	// el cluster decía «2» pero al acercarse un marcador tapaba al otro y solo
	// se veía uno. Se agrupan por coordenada (~1 m) y se apilan en vertical
	// alrededor del punto, así cada uno se ve y se puede seleccionar.
	const hotelMarkers = computed(() => {
		const groups = new Map<string, Hotel[]>()
		for (const hotel of hotelsWithValidCoordinates.value) {
			const key = `${parseFloat(hotel.longitude).toFixed(5)},${parseFloat(hotel.latitude).toFixed(5)}`
			const group = groups.get(key)
			if (group) group.push(hotel)
			else groups.set(key, [hotel])
		}

		return [...groups.values()].flatMap((group) => {
			const lngLat: [number, number] = [
				parseFloat(group[0].longitude),
				parseFloat(group[0].latitude),
			]
			return group.map((hotel, index) => ({
				hotel,
				lngLat,
				offset: [0, (index - (group.length - 1) / 2) * MARKER_STACK_GAP] as [
					number,
					number,
				],
			}))
		})
	})

	const validCenter = computed(() => {
		const lng = Number(searchContext.value?.longitude ?? form.value.longitude)
		const lat = Number(searchContext.value?.latitude ?? form.value.latitude)
		if (isNaN(lng) || isNaN(lat) || (lng === 0 && lat === 0)) {
			// Default to Mexico City if no valid coordinates
			return [-99.1332, 19.4326]
		}
		return [lng, lat]
	})

	const hasValidCoordinates = computed(() => {
		const lng = Number(form.value.longitude)
		const lat = Number(form.value.latitude)
		return !isNaN(lng) && !isNaN(lat) && !(lng === 0 && lat === 0)
	})

	const mapboxInstance = computed(() => {
		return mapboxRef.value?.map
	})

	const handleCreateMapInstance = (instance: any) => {
		mapboxRef.value = { map: instance }
		setupMapLayers(instance)
	}

	const setupMapLayers = (map: any) => {
		map.on('load', () => {
			// Agregar fuente de datos para los hoteles con clustering
			map.addSource('hotels', {
				type: 'geojson',
				data: {
					type: 'FeatureCollection',
					features: [],
				},
				cluster: true,
				clusterMaxZoom,
			})

			updateHotelClusters()

			// Si ya hay coordenadas válidas, dibujar el círculo
			if (hasValidCoordinates.value) {
				updateSearchRadius(validCenter.value[0], validCenter.value[1])
			}

			// Las capas de cluster/punto se apagan (maxzoom) justo donde entran los
			// marcadores de precio (shouldShowMarkers). Antes el source seguía
			// agrupando hasta zoom 14.99 y entre 14 y 15 se pintaban los dos a la
			// vez: el círculo «2» encima de los marcadores.

			// Capa para los clusters (círculos azules con el número de hoteles);
			// el tamaño crece con la cantidad para distinguir 2 de 40 de un vistazo
			map.addLayer({
				id: 'clusters',
				type: 'circle',
				source: 'hotels',
				filter: ['has', 'point_count'],
				maxzoom: clusterMaxZoom,
				paint: {
					'circle-color': '#2563eb',
					'circle-radius': ['step', ['get', 'point_count'], 20, 10, 25, 50, 30],
					'circle-stroke-width': 3,
					'circle-stroke-color': '#ffffff',
					'circle-opacity': 0.9,
				},
			})

			// Hotel que no quedó en ningún cluster: antes no tenía capa y a zoom
			// bajo simplemente no aparecía en el mapa
			map.addLayer({
				id: 'unclustered-hotels',
				type: 'circle',
				source: 'hotels',
				filter: ['!', ['has', 'point_count']],
				maxzoom: clusterMaxZoom,
				paint: {
					'circle-color': '#2563eb',
					'circle-radius': 8,
					'circle-stroke-width': 3,
					'circle-stroke-color': '#ffffff',
				},
			})

			// Capa para el número de hoteles en el cluster
			map.addLayer({
				id: 'cluster-count',
				type: 'symbol',
				source: 'hotels',
				filter: ['has', 'point_count'],
				maxzoom: clusterMaxZoom,
				layout: {
					'text-field': '{point_count_abbreviated}',
					'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
					'text-size': 14,
				},
				paint: {
					'text-color': '#ffffff',
				},
			})

			// Click en clusters para hacer zoom y expandir
			map.on('click', 'clusters', (e: any) => {
				const features = map.queryRenderedFeatures(e.point, {
					layers: ['clusters'],
				})
				const clusterId = features[0].properties.cluster_id
				const source = map.getSource('hotels') as any

				// Obtener el zoom ideal para expandir el cluster
				source.getClusterExpansionZoom(clusterId, (err: any, zoom: number) => {
					if (err) return

					map.easeTo({
						center: features[0].geometry.coordinates,
						zoom: zoom,
						duration: 500,
					})
				})
			})

			// Click en un hotel suelto: se acerca y lo selecciona como en la lista
			map.on(
				'click',
				'unclustered-hotels',
				(e: { features?: { properties?: { hotelId?: string } }[] }) => {
					const hotelId = e.features?.[0]?.properties?.hotelId
					const hotel = hotelsWithValidCoordinates.value.find(
						(item) => item.id === hotelId
					)
					if (hotel) centerActiveHotel(hotel)
				}
			)

			// Cambiar cursor al pasar sobre clusters y hoteles sueltos
			for (const layer of HOTEL_LAYERS) {
				map.on('mouseenter', layer, () => {
					map.getCanvas().style.cursor = 'pointer'
				})
				map.on('mouseleave', layer, () => {
					map.getCanvas().style.cursor = 'crosshair'
				})
			}

			// Actualizar zoom actual cuando cambie
			map.on('zoom', () => {
				currentZoom.value = map.getZoom()
			})

			// Inicializar zoom actual
			currentZoom.value = map.getZoom()

			// El círculo usa el radio efectivo que confirmó el servidor.
			const initialCircleData =
				hasValidCoordinates.value && searchRadiusKm.value ?
					{
						type: 'FeatureCollection' as const,
						features: [
							{
								type: 'Feature' as const,
								geometry: {
									type: 'Polygon' as const,
									coordinates: [
										createCircle(
											[validCenter.value[0], validCenter.value[1]],
											searchRadiusKm.value
										),
									],
								},
								properties: {},
							},
						],
					}
				:	{
						type: 'FeatureCollection' as const,
						features: [],
					}

			map.addSource('search-radius', {
				type: 'geojson',
				data: initialCircleData,
			})

			// Capa para el relleno del círculo de búsqueda (tenue: el área se marca,
			// pero no compite con los marcadores de hotel que quedan encima)
			map.addLayer({
				id: 'search-radius-fill',
				type: 'fill',
				source: 'search-radius',
				paint: {
					'fill-color': '#3b82f6',
					'fill-opacity': 0.1,
				},
			})

			// Capa para el contorno del círculo de búsqueda (muy prominente)
			map.addLayer({
				id: 'search-radius-outline',
				type: 'line',
				source: 'search-radius',
				paint: {
					'line-color': '#1d4ed8',
					'line-width': 5,
					'line-dasharray': [8, 4],
					'line-blur': 0.5,
				},
			})
		})
	}

	const createCircle = circlePolygon

	const updateSearchRadius = (lng: number, lat: number) => {
		const map = mapboxInstance.value
		if (!map) return

		const source = map.getSource('search-radius') as any
		if (source) {
			const circleCoords =
				searchRadiusKm.value ?
					createCircle([lng, lat], searchRadiusKm.value)
				:	null
			source.setData({
				type: 'FeatureCollection',
				features:
					circleCoords ?
						[
							{
								type: 'Feature',
								geometry: {
									type: 'Polygon',
									coordinates: [circleCoords],
								},
								properties: {},
							},
						]
					:	[],
			})
		}
	}

	const updateHotelClusters = () => {
		const map = mapboxInstance.value
		if (!map) return

		const source = map.getSource('hotels') as any
		if (source) {
			const features = hotelsWithValidCoordinates.value.map((hotel) => ({
				type: 'Feature' as const,
				geometry: {
					type: 'Point' as const,
					coordinates: [
						parseFloat(hotel.longitude),
						parseFloat(hotel.latitude),
					],
				},
				properties: {
					hotelId: hotel.id,
					hotelName: hotel.name,
				},
			}))

			source.setData({
				type: 'FeatureCollection',
				features: features,
			})
		}
	}

	const handleMapClick = async (event: any) => {
		if (isSearchingInMap.value) return
		// Prevenir ejecución si se hace clic en marcadores u otros controles
		if (event.originalEvent?.defaultPrevented) {
			return
		}

		// Ignorar clics en controles de navegación o marcadores
		const target = event.originalEvent?.target
		if (target) {
			if (
				target.closest('.mapboxgl-ctrl') ||
				target.closest('.mapboxgl-marker')
			) {
				return
			}
		}

		// Un clic sobre un cluster o un hotel del canvas lo atiende su propia
		// capa (zoom / seleccionar); sin esto también abría «¿Cambiar ubicación?»
		const map = mapboxInstance.value
		const hotelLayers = HOTEL_LAYERS.filter((layer) => map?.getLayer(layer))
		if (
			hotelLayers.length &&
			map.queryRenderedFeatures(event.point, { layers: hotelLayers }).length
		) {
			return
		}

		// Obtener el formulario de disponibilidad
		const availabilityForm = props.availabilityFormRef?.availabilityForm

		// Solo buscar si hay viajeros y fechas
		if (
			!availabilityForm?.travelers?.length ||
			!form.value.Start ||
			!form.value.End
		) {
			return
		}

		const { lng, lat } = event.lngLat

		// Guardar coordenadas y mostrar modal de confirmación
		pendingClickCoords.value = { lng, lat }
		showConfirmModal.value = true
	}

	const confirmSearch = async () => {
		if (!pendingClickCoords.value || isSearchingInMap.value) return

		const { lng, lat } = pendingClickCoords.value
		const availabilityForm = props.availabilityFormRef?.availabilityForm

		// Cerrar modal
		showConfirmModal.value = false

		// Marcar como búsqueda personalizada
		customMarkers.value = [{ lng, lat, id: ++markerIdCounter }]

		// Mostrar loading
		isSearchingInMap.value = true

		try {
			setFormValue('companyBranchId', undefined)
			setFormValue('placeId', undefined)
			setFormValue('destinationLabel', 'Ubicación personalizada')
			setFormValue(
				'distance',
				(
					searchContext.value?.radiusSource === 'custom' &&
						searchContext.value.allowCustomRadius
				) ?
					form.value.distance
				:	undefined
			)
			setSearchContext(null)
			if (props.availabilityFormRef) props.availabilityFormRef.clearBranch?.()
			setFormValue('longitude', lng)
			setFormValue('latitude', lat)
			setFormValue('cityName', 'Ubicación personalizada')
			setFormValue('destinationCategory', 'locality' as any)
			setFormValue(
				'users',
				availabilityForm.travelers.map((element: User) => element.id)
			)
			setFormValue(
				'memberId',
				(user.value?.internal ? availabilityForm.member?.id : user.value?.id) ??
					0
			)
			setFormValue('previousRequest', availabilityForm.previousRequest)

			paramsGeo.lat = lat
			paramsGeo.lng = lng

			// El reverse geocode solo valida el terreno: es opcional y NUNCA debe
			// impedir la búsqueda. Antes se leía results[0] sin guard, y como
			// Google responde 200 con results vacío cuando rechaza la llave
			// (REQUEST_DENIED), el TypeError caía en el catch de abajo y el
			// refetch() de hoteles jamás se ejecutaba: clic sin petición ni aviso.
			let geocode: GeocodeValidationResult | undefined
			try {
				// Ante un fallo no se usa `data`: sería el resultado cacheado de una
				// consulta previa a este mismo punto, no el del intento actual.
				const result = await refetchGeocode()
				geocode = result.isError ? undefined : result.data
			} catch (err) {
				console.warn(
					'Reverse geocode falló; se busca sin validar el terreno:',
					err
				)
			}

			const geocodeResult = geocode?.data?.results?.[0]

			if (geocodeResult?.geometry.location_type.includes('GEOMETRIC_CENTER')) {
				const terrainName =
					geocode?.terrainType === 'natural_feature' ?
						'característica natural (montaña, lago, etc.)'
					: geocode?.terrainType === 'mountain_peak' ? 'montaña'
					: geocode?.terrainType === 'park' ? 'parque'
					: geocode?.terrainType === 'colloquial_area' ?
						'área sin dirección específica'
					:	'ubicación no válida'

				toast.warning(
					`No se pueden buscar hoteles en ${terrainName}. Por favor, selecciona una ciudad o dirección específica.`,
					{}
				)
				return
			}

			await refetch()
		} catch (err) {
			console.error('Error al buscar hoteles:', err)
			toast.error('No se pudo buscar hoteles en esa ubicación.')
		} finally {
			isSearchingInMap.value = false
			pendingClickCoords.value = null
		}
	}

	const cancelSearch = () => {
		showConfirmModal.value = false
		pendingClickCoords.value = null
	}

	const openInfoWindow = (hotel: Hotel) => {
		if (selectedHotel.value?.id) {
			hotelItems.value[selectedHotel.value.id] = false
			setSelectedHotel(undefined)
		}

		hotelItems.value[hotel.id] = true

		setSelectedHotel(hotel)
	}

	const closeInfoWindow = () => {
		if (selectedHotel.value) {
			hotelItems.value[selectedHotel.value.id] = false
		}
		setSelectedHotel(undefined)
	}

	const centerActiveHotel = (hotel: Hotel) => {
		openInfoWindow(hotel)
		mapboxInstance.value?.flyTo({
			center: [parseFloat(hotel.longitude), parseFloat(hotel.latitude)],
			zoom: 16,
		})

		// Siempre hacer scroll al hotel en la lista
		moveToHotelEntry(hotel.id)

		setSelectedHotel(hotel)
	}

	const scrollToTargetAdjusted = (element: HTMLElement | null) => {
		if (element) {
			var headerOffset = 64
			var elementPosition = element.getBoundingClientRect().top
			var offsetPosition = elementPosition + window.scrollY - headerOffset

			window.scrollTo({
				top: offsetPosition,
				left: 0,
				behavior: 'smooth',
			})
		}
	}

	const moveToHotelEntry = (id: string) => {
		if (props.displayMap && displayHotels.value) {
			const item = props.itemRefs.find((element) => {
				return element.hotelId === id
			})

			const container = item?.hotelContainer

			if (container) {
				filteredHotelList.value[0]?.id === id ?
					scrollToTargetAdjusted(container as HTMLElement)
				:	container.scrollIntoView({ behavior: 'smooth', block: 'center' })
			}
		}
	}

	const getNightFare = (hotel: Hotel) => {
		return Intl.NumberFormat('es-MX', {
			style: 'currency',
			currency: 'MXN',
		}).format(
			Math.ceil(
				currencyToInt(hotel.fare.split(' ')[0]) / reservationNights.value
			)
		)
	}

	watch(filteredHotelList, () => {
		updateHotelClusters()
	})

	watch([validCenter, searchRadiusKm], ([[lng, lat]]) => {
		const numLng = Number(lng)
		const numLat = Number(lat)
		if (
			!isNaN(numLng) &&
			!isNaN(numLat) &&
			!(numLng === 0 && numLat === 0) &&
			mapboxInstance.value
		) {
			updateSearchRadius(numLng, numLat)
		}
	})

	defineExpose({
		centerActiveHotel,
		closeInfoWindow,
	})
</script>

<template>
	<!-- Map -->
	<!--
		top-0 (no solo h-full): sin un top explícito, este absolute no se pega
		al borde superior de su contenedor posicionado — toma su "posición
		estática" (donde caería en flujo normal, después de los botones/aviso
		que le preceden), que no es y=0. Con h-full sumado a ese offset, el
		div se pasaba del fondo del contenedor por esa misma distancia (~88px
		en pruebas) y ESE era el verdadero resto del scroll fantasma de página
		que sobrevivía incluso con el alto del contenedor ya bien calculado.
	-->
	<div class="absolute inset-0">
		<MapboxMap
			ref="mapboxRef"
			style="width: 100%; height: 100%; cursor: crosshair"
			:access-token="token"
			map-style="mapbox://styles/desarrolloef/clprhwrxy00d001r726l45div"
			language="auto"
			:center="validCenter"
			:zoom="14"
			@mb-created="handleCreateMapInstance"
			@mb-click="handleMapClick"
		>
			<MapboxMarker
				v-if="hasValidCoordinates"
				:lng-lat="validCenter"
			>
				<div class="relative">
					<!-- Pulso animado alrededor del marcador -->
					<div
						class="absolute inset-0 -m-2 rounded-full opacity-30 animate-ping"
						:class="customMarkers.length > 0 ? 'bg-red-600' : 'bg-blue-600'"
					></div>
					<span
						v-tooltip="
							`Centro de búsqueda${searchRadiusKm ? ` · Radio ${searchRadiusKm} km` : ''}`
						"
						class="material-symbols-outlined p-1 border-3 text-[28px] text-white border-white rounded-full cursor-pointer shadow-lg relative z-10"
						:class="customMarkers.length > 0 ? 'bg-red-600' : 'bg-blue-600'"
					>
						location_on
					</span>
				</div>
			</MapboxMarker>

			<template v-if="shouldShowMarkers">
				<MapboxMarker
					v-for="{ hotel, lngLat, offset } in hotelMarkers"
					:key="hotel.id"
					:lng-lat="lngLat"
					:offset="offset"
					aria-label="Hotel"
				>
					<HotelCustomMarker
						:hotel="hotel"
						:center-active-hotel="centerActiveHotel"
						:get-night-fare="getNightFare"
						:selected-hotel="selectedHotel"
					/>
				</MapboxMarker>
			</template>

			<MapboxNavigationControl
				position="bottom-right"
				:visualize-pitch="true"
			/>
		</MapboxMap>

		<p
			v-if="showRadiusLabel && searchRadiusKm"
			class="absolute left-3 top-3 z-10 max-w-[calc(100%-4rem)] rounded-lg bg-white/95 px-3 py-2 text-xs font-medium text-[#253C70] shadow"
			role="status"
		>
			Radio de búsqueda: {{ searchRadiusKm }} km ·
			{{
				searchContext?.radiusSource === 'branch' ? 'Sede'
				: searchContext?.radiusSource === 'custom' ? 'Personalizado'
				: searchContext?.radiusSource === 'company' ? 'Empresa'
				: 'Agencia'
			}}
		</p>

		<!-- Modal de confirmación para cambiar ubicación -->
		<Transition name="fade">
			<div
				v-if="showConfirmModal"
				class="absolute inset-0 z-30 flex items-center justify-center overflow-y-auto bg-black/50 p-3 backdrop-blur-sm"
			>
				<div
					class="w-full min-w-0 max-w-md rounded-2xl bg-white p-4 text-center shadow-2xl sm:p-6"
				>
					<div class="flex justify-center mb-4">
						<div
							class="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center"
						>
							<span class="material-symbols-outlined text-blue-600 text-3xl">
								location_on
							</span>
						</div>
					</div>
					<h3 class="text-lg font-bold text-gray-800 mb-2">
						¿Cambiar ubicación de búsqueda?
					</h3>
					<p class="text-sm text-gray-600 mb-6">
						¿Estás seguro de que deseas buscar hoteles en esta nueva ubicación?
						Se realizará una nueva búsqueda con las fechas y viajeros actuales.
					</p>
					<div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
						<button
							@click="cancelSearch"
							class="px-5 py-2.5 rounded-lg border-2 border-gray-300 text-gray-700 font-semibold hover:bg-gray-100 transition-colors"
						>
							Cancelar
						</button>
						<button
							@click="confirmSearch"
							class="px-5 py-2.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
						>
							<span class="material-symbols-outlined text-lg">search</span>
							Buscar aquí
						</button>
					</div>
				</div>
			</div>
		</Transition>

		<!-- Loading overlay para búsqueda en mapa (minimizable) -->
		<Transition name="fade">
			<div
				v-if="isSearchingInMap && !isSearchMinimized"
				class="absolute inset-0 z-20 flex items-center justify-center overflow-y-auto bg-black/40 p-3 backdrop-blur-sm"
			>
				<div
					class="relative w-full min-w-0 max-w-sm rounded-2xl bg-white p-4 text-center shadow-2xl sm:p-6"
				>
					<div class="flex justify-center mb-4">
						<div class="relative">
							<div
								class="w-16 h-16 border-4 border-blue-200 rounded-full"
							></div>
							<div
								class="absolute top-0 left-0 w-16 h-16 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"
							></div>
							<span
								class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 material-symbols-outlined text-blue-600 text-2xl"
							>
								location_searching
							</span>
						</div>
					</div>
					<h3 class="text-lg font-bold text-gray-800 mb-2">Buscando hoteles</h3>
					<p class="text-sm text-gray-600">
						Espere un momento, estamos buscando reservas disponibles en esta
						ubicación...
					</p>
				</div>
			</div>
		</Transition>

		<!-- Alerta minimizada de búsqueda -->
		<Transition name="slide-up">
			<div
				v-if="isSearchingInMap && isSearchMinimized"
				class="absolute bottom-4 left-1/2 -translate-x-1/2 z-20"
			>
				<button
					@click="isSearchMinimized = false"
					class="flex items-center gap-3 bg-white rounded-full shadow-lg px-4 py-2.5 hover:shadow-xl transition-all"
				>
					<div class="relative">
						<div
							class="w-6 h-6 border-2 border-blue-600 rounded-full border-t-transparent animate-spin"
						></div>
					</div>
					<span class="text-sm font-medium text-gray-700">Buscando...</span>
					<span
						class="material-symbols-outlined text-gray-400 text-lg"
						v-tooltip="'Expandir'"
					>
						expand_less
					</span>
				</button>
			</div>
		</Transition>
	</div>
</template>

<style scoped>
	.fade-enter-active,
	.fade-leave-active {
		transition: opacity 0.3s ease;
	}

	.fade-enter-from,
	.fade-leave-to {
		opacity: 0;
	}

	/*
		Desde lg el riel flotante (WhatsApp, comentarios) vive en la esquina inferior
		derecha y tapaba los botones de zoom. Se suben por ENCIMA del riel, no a su
		izquierda: el riel empieza 144px arriba del borde (dos círculos de 56px, 12px
		entre ellos y 20px de margen). Correrlos a la izquierda los metía en la
		columna que alcanza la tarjeta del hotel (centrada, hasta 384px): de 1280 a
		1341px, con el menú abierto, la tarjeta tapaba todo el zoom y su ✕ caía sobre
		el +. En la columna derecha la tarjeta sólo llega con un mapa de menos de
		462px. En móvil el riel va a media altura y no choca.
	*/
	@media (min-width: 1024px) {
		:deep(.mapboxgl-ctrl-bottom-right) {
			margin-bottom: 144px;
		}
	}
</style>
